import { config } from '@backoffice-frontend/common'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

type SelectedFleetsContextValue = {
  selectedFleetIds: string[]
  fleetsLoading: boolean
  selectFleetsForRegion: (regionId: string, fleetIds: string[]) => void
  setFleetsLoading: (value: boolean) => void
  getPersistedFleetsForRegion: (regionId: string) => string[]
}

const SelectedFleetsContext = createContext<SelectedFleetsContextValue | null>(
  null,
)

const useSelectedFleetsContext = () => {
  const ctx = useContext(SelectedFleetsContext)
  if (!ctx) {
    throw new Error(
      'useSelectedFleets must be used within a SelectedFleetsProvider',
    )
  }
  return ctx
}

export const useSelectedFleets = (): string[] => {
  const { selectedFleetIds } = useSelectedFleetsContext()
  return selectedFleetIds
}

export const useFleetsLoading = (): boolean => {
  const { fleetsLoading } = useSelectedFleetsContext()
  return fleetsLoading
}

export const useSelectFleetsForRegion = () => {
  const { selectFleetsForRegion } = useSelectedFleetsContext()
  return selectFleetsForRegion
}

export const useSetFleetsLoading = () => {
  const { setFleetsLoading } = useSelectedFleetsContext()
  return setFleetsLoading
}

export const useGetPersistedFleetsForRegion = () => {
  const { getPersistedFleetsForRegion } = useSelectedFleetsContext()
  return getPersistedFleetsForRegion
}

export const SelectedFleetsProvider = ({
  area,
  children,
}: {
  area: string
  children: ReactNode
}) => {
  const [selectedFleetIds, setSelectedFleetIds] = useState<string[]>([])
  const [fleetsLoading, setFleetsLoading] = useState<boolean>(true)

  const selectFleetsForRegion = useCallback(
    (regionId: string, fleetIds: string[]) => {
      const stored = config.getSelectedFleetsForArea(area)
      config.setSelectedFleetsForArea(area, { ...stored, [regionId]: fleetIds })
      setSelectedFleetIds(fleetIds)
    },
    [area],
  )

  const getPersistedFleetsForRegion = useCallback(
    (regionId: string): string[] => {
      const stored = config.getSelectedFleetsForArea(area)
      return stored[regionId] ?? []
    },
    [area],
  )

  const value = useMemo(
    () => ({
      selectedFleetIds,
      fleetsLoading,
      selectFleetsForRegion,
      setFleetsLoading,
      getPersistedFleetsForRegion,
    }),
    [
      selectedFleetIds,
      fleetsLoading,
      selectFleetsForRegion,
      getPersistedFleetsForRegion,
    ],
  )

  return (
    <SelectedFleetsContext.Provider value={value}>
      {children}
    </SelectedFleetsContext.Provider>
  )
}
